import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Award, GraduationCap, Loader2, Trophy, Users as UsersIcon, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";

const ROLE_LABEL = {
  admin: "Admin",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  installer: "Installation",
};

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

const statusLabel = (s) => ({
  in_progress: "läuft",
  abandoned: "abgebrochen",
  submitted: "abgegeben",
  submitted_late: "verspätet",
}[s] || s);

export default function AdminQuizResults() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [config, setConfig] = useState({ total_questions: 30, pass_threshold: 24 });
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "sales_director") {
      toast.error("Nur Admin & Vertriebsdirektor.");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/quiz/admin/attempts");
      setSummary(data.summary || []);
      setAttempts(data.attempts || []);
      setConfig(data.config || config);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auswertung konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const userAttempts = selectedUser
    ? attempts.filter((a) => a.user_id === selectedUser.user_id)
    : [];

  const downloadCert = async (attemptId) => {
    try {
      const resp = await api.get(
        `/quiz/attempts/${attemptId}/certificate`,
        { responseType: "blob", timeout: 60_000 }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Novatus_Zertifikat.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Zertifikat heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400">
        <Loader2 className="animate-spin mr-2" /> Lade Auswertung…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 px-4 py-5 max-w-5xl mx-auto" data-testid="admin-quiz-results-page">
      <header className="flex items-center gap-3 mb-6">
        <button
          onClick={() => selectedUser ? setSelectedUser(null) : navigate("/dashboard")}
          className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
          data-testid="quiz-results-back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-wider text-[#00E575]/80 font-bold">
            Direktorat
          </div>
          <h1 className="font-display text-2xl font-bold leading-tight">
            {selectedUser ? `Versuche von ${selectedUser.user_name || "—"}` : "Quiz-Auswertung"}
          </h1>
        </div>
        <button
          onClick={load}
          className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
          data-testid="quiz-results-refresh-btn"
          title="Aktualisieren"
        >
          <RefreshCw size={16} />
        </button>
      </header>

      {!selectedUser && (
        <>
          <section className="bg-[#0F1014] border border-[#00E575]/25 rounded-2xl p-4 mb-5 flex items-start gap-3">
            <GraduationCap className="text-[#00E575] flex-shrink-0 mt-0.5" size={20} />
            <div className="text-[13px] text-zinc-300 leading-relaxed">
              30 Multiple-Choice-Fragen · Bestehensgrenze <b className="text-[#00E575]">{config.pass_threshold}/{config.total_questions}</b>{" "}
              · 25 Minuten Zeitlimit. Jede Person darf beliebig oft versuchen – alle Versuche werden hier protokolliert.
            </div>
          </section>

          {summary.length === 0 ? (
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400">
              Noch keine Quiz-Versuche im System.
            </div>
          ) : (
            <section className="space-y-2" data-testid="quiz-summary-list">
              <h2 className="font-display text-base font-bold mb-3 text-zinc-300 flex items-center gap-2">
                <UsersIcon size={16} /> Mitarbeiter · {summary.length}
              </h2>
              {summary.map((row) => (
                <button
                  key={row.user_id}
                  onClick={() => setSelectedUser(row)}
                  data-testid={`quiz-summary-row-${row.user_id}`}
                  className="w-full flex items-center justify-between bg-[#18181B] border border-zinc-800 rounded-2xl px-5 py-4 hover:border-[#00E575]/50 transition text-left active:scale-[0.99]"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[14px] text-zinc-100 truncate">{row.user_name || "—"}</div>
                    <div className="text-[12px] text-zinc-300 mt-0.5">
                      {ROLE_LABEL[row.user_role] || row.user_role} · {row.attempts} Versuch{row.attempts === 1 ? "" : "e"} · zuletzt {formatDateTime(row.last_attempt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {row.best_score !== null && row.best_score !== undefined ? (
                      <div className={`font-mono text-[16px] font-bold ${row.ever_passed ? "text-[#00E575]" : "text-amber-400"}`}>
                        {row.best_score}/{config.total_questions}
                      </div>
                    ) : (
                      <div className="text-[12px] text-zinc-400 font-medium">—</div>
                    )}
                    {row.ever_passed && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-bold bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30">
                        <Trophy size={11} /> bestanden
                      </span>
                    )}
                    <ChevronRight size={18} className="text-zinc-500" />
                  </div>
                </button>
              ))}
            </section>
          )}
        </>
      )}

      {selectedUser && (
        <section className="space-y-3" data-testid="quiz-user-detail">
          <div className="bg-[#0F1014] border border-zinc-800 rounded-2xl p-4 text-[13px] text-zinc-300">
            <div className="flex items-center gap-2 font-bold text-zinc-100 mb-1">
              <UsersIcon size={14} className="text-[#00E575]" />
              {selectedUser.user_name}
            </div>
            <div className="text-[12px] text-zinc-300 font-medium">
              {ROLE_LABEL[selectedUser.user_role] || selectedUser.user_role} ·{" "}
              {selectedUser.attempts} Versuch{selectedUser.attempts === 1 ? "" : "e"} insgesamt
              {selectedUser.best_score !== null && selectedUser.best_score !== undefined && (
                <> · bester Score <b className="text-zinc-100">{selectedUser.best_score}/{config.total_questions}</b></>
              )}
              {selectedUser.ever_passed && (
                <span className="ml-2 text-[#00E575] font-bold">✓ bestanden</span>
              )}
            </div>
          </div>

          {userAttempts.length === 0 ? (
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center text-zinc-400">
              Keine Versuche.
            </div>
          ) : (
            userAttempts.map((a) => (
              <div
                key={a.id}
                data-testid={`quiz-user-attempt-${a.id}`}
                className="bg-[#18181B] border border-zinc-800 rounded-2xl px-5 py-4 flex items-center justify-between"
              >
                <div className="text-[13px]">
                  <div className="font-mono text-zinc-200">{formatDateTime(a.started_at)}</div>
                  <div className="text-[12px] text-zinc-300 mt-1">
                    {statusLabel(a.status)}
                    {a.time_spent_s !== null && a.time_spent_s !== undefined && (
                      <> · {Math.floor(a.time_spent_s / 60)} min {a.time_spent_s % 60} s</>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {a.score !== null && a.score !== undefined ? (
                    <div className={`font-mono text-[16px] font-bold ${a.passed ? "text-[#00E575]" : "text-amber-400"}`}>
                      {a.score}/{a.total}
                    </div>
                  ) : (
                    <div className="text-[12px] text-zinc-400 font-medium">—</div>
                  )}
                  {a.passed === true && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-bold bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30">
                      <Trophy size={11} /> bestanden
                    </span>
                  )}
                  {a.passed === false && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-bold bg-zinc-800 text-zinc-400 border border-zinc-700">
                      nicht bestanden
                    </span>
                  )}
                  {a.passed === true && (
                    <button
                      onClick={() => downloadCert(a.id)}
                      data-testid={`admin-cert-${a.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-bold bg-[#00E575]/10 text-[#00E575] border border-[#00E575]/40 hover:bg-[#00E575]/20 active:scale-[0.98] transition"
                      title="Zertifikat als PDF herunterladen"
                    >
                      <Award size={11} /> PDF
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </section>
      )}
    </div>
  );
}
